import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, signal, inject } from "@angular/core";
import { EpisodeService } from "@project/episode/episode.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { IEpisode } from "@project/models/episodes/episode";
import { ConfirmationService, MenuItem, MessageService } from "primeng/api";
import { OverlayPanel } from "primeng/overlaypanel";
import { ConsultationMergeMoveService } from "@project/consultation-merge-move/consultation-merge-move.service";
import { map } from "rxjs";
import { EpisodeStatus } from "@project/models/episodes/episode-status";
import { HistoricalItem } from "@mipro/classes/historical-item";
import { FileDetails } from "@main/file-storage";
import { UserService, UserTitle } from "@main/users";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { FormIODataService } from "@features/bb-formio/services/formio-data.service";
import { MultiUserFormAssociationsService } from "@main/formio/multi-user-form/services/multi-user-form-associations.service";
import {
    EpisodeMultiUserFormAssociation,
    MultiUserFormAssociation
} from "@main/formio/multi-user-form/dto/multi-user-form-associations.dto";
import { MultiUserFormStagePermissionAction } from "@main/formio/multi-user-form/dto/multi-user-form-stage.dto";
import { IEpisodeFormData } from "@project/models/episodes/episode_formdata";
import { CoreRole } from "@main/roles";
import { Permissions } from "@main/permissions/permissions";
import { FormIOCategoryService } from "@main/formio/services/formioCategory.service";
import { FormRevisionDTO } from "../../../features/bb-formio/dto/form-revision";
import { FormIORevisionService } from "../../../features/bb-formio/services/formio-revision.service";
import { FormPdfExportMode } from "../../../features/bb-formio/components/bb-form-builder/bb-form-builder.models";
import { FormioVariableProviderService } from "../../../main/formio/services/formio-variable-provider.service";
import { IStaticVariable } from "../../../main/reporting.v3/core/variables/variable-models";
import { IFormDataVariable } from "../../../main/formio/formio.models";
import { ReportingDashboardComponent } from "../../../main/reporting.v3/dashboard/components/dashboard.component";
import { AsyncMenuItem } from "@project/prescription-shared/async-menu";
import { Message } from "@bbwt/classes/message";

export interface UserInfo {
    title?: UserTitle;
    fullName: string;
    roles: string;
    organization: string;
}

@Component({
    selector: "episode-form-ribbon",
    templateUrl: "./episode-form-ribbon.component.html",
    styleUrls: ["./episode-form-ribbon.component.scss"]
})
export class EpisodeFormRibbonComponent implements OnInit {
    private readonly episodeService = inject(EpisodeService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly _cd = inject(ChangeDetectorRef);
    private readonly mergeMoveService = inject(ConsultationMergeMoveService);
    private readonly userHelperService = inject(UserHelperService);
    private readonly sanitizer = inject(DomSanitizer);
    private readonly formIODataService = inject(FormIODataService);
    private readonly formioVariableProvider = inject(FormioVariableProviderService);
    private readonly formIOCategoryService = inject(FormIOCategoryService);
    private readonly userService = inject(UserService);
    private readonly messageService = inject(MessageService);

    sharingFile = false;
    sharingFileId: string = null;
    fileCreatedDate: string;

    episodeDocumentShowHistorical = signal(false);
    episodeDocumentHistoricalItems = signal<HistoricalItem[]>([]);
    historyLoading = signal(false);
    isLoadingForm = signal(false);
    isFormLoaded = signal(false);
    isCustomFormLoaded = signal(false);
    isArchived = false;

    usersInfo: UserInfo[]= [];

    displayDocumentDialog = false;
    createdByOrganization: string;
    menuOptions: AsyncMenuItem[];

    @Input() showEllipsisButton: boolean = true;
    @Input() showActionButtons: boolean = true;
    @Input() moveButtonVisible: boolean = true;
    @Input() form: IEpisodeFormData;
    @Input() episode: IEpisode;
    @Input() deletable: boolean = true;
    @Input() hideFormsTitle: boolean = false;

    isActiveEpisode = false;
    isEpisodeLocked = false;
    fileDetails: FileDetails = null;
    fileDetailsId: string = null;
    isMuf = false;
    formName: string
    createdByUserName: string;
    createdByOrganisationName : string;
    creationDate: string
    urlPdf: string = null;
    categoryForm: string;
    canDelete = true;

    activeRevision: FormRevisionDTO = null;
    pdfExportMode: FormPdfExportMode = FormPdfExportMode.Auto;
    protected readonly FormPdfExportMode = FormPdfExportMode;
    displayPreviewCustomPdf: boolean;
    customPdfDashboardId: string;
    customPdfVariables: IStaticVariable[] = [];
    formDataVariables: IFormDataVariable[] = [];
    isFullCustomPdfView: boolean = false;

    iframeHeight: string = "200px"
    iframeWidth: string = "860px"

    muFormAssoc: EpisodeMultiUserFormAssociation = null;
    safePdfUrl: SafeResourceUrl;
    isFullView: boolean = false;

    get isClinicalSafetyOfficer(){
        return this.userHelperService.hasRole(CoreRole.ClinicalSafetyOfficer)
    }

    @Output() readonly delete = new EventEmitter<string>();
    @Output() readonly refresh = new EventEmitter<void>();
    @Output() readonly onCustomPdfPreview = new EventEmitter<any>();

    @ViewChild(OverlayPanel) userOverlayInfo: OverlayPanel;
    @ViewChild("rd1") protected customPdfDashboard: ReportingDashboardComponent;
    @ViewChild("asyncMenu") asyncMenu: any;

    async ngOnInit() {
        try {
            this.isActiveEpisode = this.episode?.state === EpisodeStatus.Active;
            this.isEpisodeLocked = this.episode?.state === EpisodeStatus.Locked;
            this.isArchived = !!this.form?.isArchived;
            this.formName = this.form?.formDefinition?.name;
            this.categoryForm = this.form?.formDefinition?.formCategory?.name;
            this.createdByUserName = this.form?.userFullName;

            this.activeRevision = this.form?.formDefinition?.activeRevision;
            this.pdfExportMode = this.activeRevision?.pdfExportMode ?? FormPdfExportMode.Auto;

            if (this.pdfExportMode === FormPdfExportMode.CustomPDF) {
                this.customPdfDashboardId = this.activeRevision?.linkedDashboardId;
            }

            const moduleName = this.form?.formDefinition?.formCategory?.name;
            let canWriteModule = false;
            try {
                canWriteModule = this.formIOCategoryService.getWritePermissionsForCategory(moduleName)
                    ? this.userHelperService.hasPermission(Permissions.WriteFormsInstances)
                    : this.userHelperService.hasPermission(`${moduleName} - Write`);
            } catch {
                canWriteModule = this.userHelperService.hasPermission(Permissions.WriteFormsInstances);
            }

            this.canDelete = !this.form?.isArchived && this.deletable && (this.userHelperService.isTopAdmin || canWriteModule);

            this.setFormInfo();
            this.createFormOptions();
        } finally {
            this._cd.detectChanges();
        }
    }

    async initCustomPdfPreview() {
        if (!this.formDataVariables || this.formDataVariables.length === 0) {
            this.formDataVariables = await this.formioVariableProvider.getFormDataVariables();
        }

        this.customPdfVariables = this.formDataVariables?.map(variable => <IStaticVariable>{
            name: variable.name,
            $type: variable.type,
            value: this.form[variable.formDataField] ?? null
        });
    }

    async toggleCustomFormVisibility() {
        if (this.isCustomFormLoaded()) {
            this.isCustomFormLoaded.set(false);
        } else {
            if (!this.customPdfVariables || this.customPdfVariables.length === 0) {
                await this.initCustomPdfPreview();
            }
            this.isCustomFormLoaded.set(true);
        }
        this._cd.detectChanges();
    }

    async onPreviewCustomPdf() {
        if (!this.customPdfVariables || this.customPdfVariables.length === 0) {
            await this.initCustomPdfPreview();
        }
        this.onCustomPdfPreview.emit({ formDataId: (<any>this.form).id_original, dashboardId: this.activeRevision.linkedDashboardId} );
    }

    async loadForm() {
        if (this.isFormLoaded() || this.isLoadingForm()) {
            return;
        }

        this.isLoadingForm.set(true);

        try {
            if (!this.urlPdf && this.fileDetailsId) {
                const fileDetails = await this.userService.getFileDetails(this.fileDetailsId);

                if (fileDetails?.url) {
                    this.urlPdf = fileDetails.url;
                    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.urlPdf);
                    this.isFormLoaded.set(true);
                } else {
                    this.messageService.add(Message.Error("The requested file has probably been deleted.", "File not available."));
                }
            } else if (this.urlPdf) {
                this.isFormLoaded.set(true);
            }
        } catch (error) {
            this.messageService.add(Message.Error("Failed to load form.", "Error"));
        } finally {
            this.isLoadingForm.set(false);
        }
    }

    async viewForm() {
        if (this.isLoadingForm()) {
            return;
        }

        this.isLoadingForm.set(true);

        try {
            let url = this.urlPdf;

            if (!url && this.fileDetailsId) {
                const fileDetails = await this.userService.getFileDetails(this.fileDetailsId);

                if (fileDetails?.url) {
                    url = fileDetails.url;
                    this.urlPdf = url;
                    this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
                } else {
                    this.messageService.add(Message.Error("The requested file has probably been deleted.", "File not available."));
                    return;
                }
            }

            if (url) {
                window.open(url, "_blank");
            }
        } catch (error) {
            this.messageService.add(Message.Error("Failed to load form.", "Error"));
        } finally {
            this.isLoadingForm.set(false);
        }
    }

    share() {
        this.sharingFileId = this.fileDetailsId;
        this.sharingFile = true;
    }

    toggleFullView() {
        this.isFullView = !this.isFullView;
        this.iframeHeight = this.isFullView ? "100vh" : "200px";
    }

    toggleCustomPdfFullView() {
        this.isFullCustomPdfView = !this.isFullCustomPdfView;
    }

    archive() {
        this.confirmationService.confirm({
            message: "Are you sure you want to delete this Form?",
            accept: async () => {
                this.isArchived = true;
                await this.formIODataService.delete(this.form.id);
                this.delete.emit(this.form.id);
                this._cd.detectChanges();
            }
        });
    }

    showOverlayPanel(event: Event) {
        this.userOverlayInfo.show(event);
    }

    hideOverlayPanel() {
        this.userOverlayInfo.hide();
    }

    get showFileActions$() {
        return this.mergeMoveService.isMergeMoveModeOn$.pipe(
            map(on => !on && !this.form?.isArchived && this.showActionButtons )
        );
    }

    get showMoreOptions$() {
        return this.mergeMoveService.isMergeMoveModeOn$.pipe(map(on => !on && !this.isArchived));
    }

    private setFormInfo() {
        if (!this.form) {
            return;
        }

        this.creationDate = this.episodeService.formatDate(this.form.createdOn);
        this.fileDetails = this.form.fileDetails;
        this.fileDetailsId = this.form.fileDetailsId;
        this.isArchived = this.form.isArchived;

        // Don't load URL immediately - load on demand
        // this.urlPdf = this.fileDetails.url;
        // this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.urlPdf);

        this.formName = this.form.formDefinition?.name;
        this.categoryForm = this.form.formDefinition?.formCategory?.name;
        this.muFormAssoc = this.form.multiUserFormAssociations;

        this.createdByOrganisationName = this.form.organizationName;

        if (this.muFormAssoc != null) {
            this.isMuf = true;
            this.formName = this.muFormAssoc.description;
            let links = this.muFormAssoc.multiUserFormAssociationLinks ?? [];
            links.forEach(link => {
                let hasPermission = link.multiUserFormStage?.multiUserFormStagePermissions?.some(
                    permission => permission.action === MultiUserFormStagePermissionAction.Edit
                );
                if (hasPermission && link.userFullName) {
                    let rolesString = (link.userRoleNames ?? []).join("#");
                    let role = this.episodeService.getRole(rolesString);
                    const userInfo: UserInfo = {
                        title: link.userTitle,
                        fullName: link.userFullName,
                        roles: role,
                        organization: ""
                    }
                    this.usersInfo.push(userInfo);
                }
            });
        } else {
            this.createdByUserName = this.form.userFullName;
        }
    }

    private createFormOptions() {
        this.menuOptions = <AsyncMenuItem[]>[
            {
                id: "history",
                label: "History",
                icon: "pi pi-history",
                loading: this.historyLoading,
                asyncAction: async (event: Event) => {
                    this.historyLoading.set(true);
                    try {
                        const items = await this.formIODataService.getHistoricalItems(this.form.id);
                        this.episodeDocumentHistoricalItems.set(items);
                        this.episodeDocumentShowHistorical.set(true);
                    } finally {
                        this.historyLoading.set(false);
                    }
                }
            }
        ];
    }

    get iconClassName() {
        return window.innerWidth < 576 ? "small" : "episode";
    }

    get draftLayout() {
        return this.isActiveEpisode;
    }
}